import React, { useState, useEffect } from 'react';
import api from "../../apis/EdisonApi";
import { toast } from "react-toastify";
import { faPlug, faLightbulb, faBoltLightning, faChevronUp, faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const BTicino = () => {
    const [status, setStatus] = useState('NA');    

    useEffect(() => {
        const controller = new AbortController();
        getStatus();

        return () => {
            controller.abort();
        };
    }, []);

    const getStatus = async () => {
        let config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: localStorage.getItem('idToken')
            },
        };

        try {
            const resp = await api.get('/bticino/status', config);
            setStatus(resp.data);
        } catch {
            toast.error('API error getting BTicino status, please refresh the page');
        }
    };

    const renderSmartMeter = () => {
        if (status === 'NA') {
            return <div className="count">NA</div>;
        }

        return <div className="count">{status.modules.NLPC.power} W</div>;
    };

    const renderRollingShutters = () => {
        if (status === 'NA') {
            return <div className="count">NA</div>;
        }

        return <div className="count">{status.modules.NLLV.current_position}</div>;
    };

    const renderLights = () => {
        if (status === 'NA') {
            return <div className="count">NA</div>;
        }

        return <div className="count">{status.modules.NLPT.on ? "ON" : "OFF"}</div>;
    };

    const renderLightsConsumption = () => {
        if (status === 'NA') {
            return <div className="count">NA</div>;
        }

        return <div className="count">{status.modules.NLPT.power} W</div>;
    };

    const renderPlug = () => {
        if (status === 'NA') {
            return <div className="count">NA</div>;
        }

        return <div className="count">{status.modules.NLPO.on ? "ON" : "OFF"}</div>;
    };

    const renderPlugConsumption = () => {
        if (status === 'NA') {
            return <div className="count">NA</div>;
        }

        return <div className="count">{status.modules.NLPO.power} W</div>;
    };

    const toggleLights = async () => {
        try {
            toast.info('Toggling lights...');
            const resp = await api.post('/bticino/toggle-relay', {});
    
            if (resp.status !== 200) {
                const errstr = ['API: ', resp.status, resp.message].join(' ');
                toast.error(errstr);
                return;
            }
    
            setStatus({
                ...status,
                modules: {
                    ...status.modules,
                    NLPT: {
                        ...status.modules.NLPT,
                        on: !status.modules.NLPT.on,
                    },
                },
            });

            toast.success("Toggled lights successfully!");
        } catch {
            toast.error('API CORS error!');
        }
    };

    const togglePlug = async () => {
        try {
            toast.info('Toggling plug...');
            const resp = await api.post('/bticino/toggle-plug', {});
    
            if (resp.status !== 200) {
                toast.error(`API error: ${resp.status} ${resp.statusText || ''}`.trim());
                return;
            }
    
            setStatus({
                ...status,
                modules: {
                    ...status.modules,
                    NLPO: {
                        ...status.modules.NLPO,
                        on: !status.modules.NLPO.on,
                    },
                },
            });
            toast.success('Toggled plug successfully!');
        } catch {
            toast.error('API CORS error!');
        }
    };

    const renderLightBulbIcon = () => {
        if (status === 'NA' || !status.modules.NLPT.on) {
            return <FontAwesomeIcon icon={faLightbulb} size="3x" />;
        }

        return <FontAwesomeIcon style={{color: "green"}} icon={faLightbulb} size="3x" />;
    };

    const renderPlugIcon = () => {
        if (status === 'NA' || !status.modules.NLPO.on) {
            return <FontAwesomeIcon icon={faPlug} size="3x" />;
        }

        return <FontAwesomeIcon style={{color: "green"}} icon={faPlug} size="3x" />;
    };

    const objToString = (obj) => {

        let elements = [];
        for (const [key, value] of Object.entries(obj)) {
            elements.push(`${key}: ${value}`);
        }
        return '{ ' + elements.join(', ') + ' }';
    };

    const renderDetails = () => {
        if (status === 'NA')  {
            return (
                <div className="x_content">
                    <div>Device ID: bticino1</div>
                    <div>Smart Meter (NLPC): NA</div>
                    <div>Rolling Shutters (NLLV): NA</div>
                    <div>Lights (NLPT): NA</div>
                    <div>Smart Plug (NLPO): NA</div>
                </div>
            );
        } else {
            return (
                <div className="x_content">
                    <div>Device ID: bticino1</div>
                    <div>Smart Meter (NLPC): {objToString(status.modules.NLPC)}</div>
                    <div>Rolling Shutters (NLLV): {objToString(status.modules.NLLV)}</div>
                    <div>Lights (NLPT): {objToString(status.modules.NLPT)}</div>
                    <div>Smart Plug (NLPO): {objToString(status.modules.NLPO)}</div>
                    <div>Timestamp: {new Date(status.timestamp * 1000).toLocaleString()}</div>
                </div>
            );
        }
    };

    return (
        <div className="right_col" role="main">
            <header className="page-heading device-page-heading">
                <div>
                    <p className="eyebrow">CONNECTED DEVICE</p>
                    <h1>BTicino</h1>
                </div>
            </header>

            <div className="page-title">
                <div className="row top_tiles">
                    <div className="animated flipInY col-lg-4 col-md-4 col-sm-12 col-xs-12">
                        <div className="tile-stats">
                            <h3>Smart Meter</h3>
                            <div className="icon"><FontAwesomeIcon icon={faBoltLightning} size="3x" /></div>
                            { renderSmartMeter() }              
                        </div>
                    </div>
                    <div className="animated flipInY col-lg-4 col-md-4 col-sm-12 col-xs-12">
                        <div className="tile-stats">
                            <h3>Rolling Shutters Position</h3>
                            { renderRollingShutters() }
                        </div>
                    </div>            
                    <div className="animated flipInY col-lg-4 col-md-4 col-sm-12 col-xs-12">
                        <button className="tile-stats tile-stats--interactive" type="button" onClick={toggleLights}>
                            <h3>Lights</h3>
                            <div className="icon">{renderLightBulbIcon()}</div> 
                            { renderLights() }
                        </button>
                    </div>
                    <div className="animated flipInY col-lg-4 col-md-4 col-sm-12 col-xs-12">
                        <div className="tile-stats">
                            <h3>Lights Consumption</h3>
                            <div className="icon"></div> 
                            { renderLightsConsumption() }
                        </div>
                    </div>
                    <div className="animated flipInY col-lg-4 col-md-4 col-sm-12 col-xs-12">
                        <button className="tile-stats tile-stats--interactive" type="button" onClick={togglePlug}>
                            <h3>Plug</h3>
                            <div className="icon">{renderPlugIcon()}</div> 
                            { renderPlug() }
                        </button>
                    </div>
                    <div className="animated flipInY col-lg-4 col-md-4 col-sm-12 col-xs-12">
                        <div className="tile-stats">
                            <h3>Plug Consumption</h3>
                            <div className="icon"></div> 
                            { renderPlugConsumption() }
                        </div>
                    </div>
                </div>
            </div>


            <div className="row">
                <div className="col-xs-12">
                    <div className="x_panel">
                        <div className="x_title">
                            <h2>Details</h2>
                            <ul className="nav navbar-right panel_toolbox">
                                <li><span id="collapse-link-details" className="collapse-link"><FontAwesomeIcon icon={faChevronUp} size="lg" /></span>
                                </li>
                                <li><span className="close-link"><FontAwesomeIcon icon={faClose} size="lg" /></span>
                                </li>
                            </ul>
                            <div className="clearfix"></div>
                        </div>

                        { renderDetails() }

                    </div>
                </div>
            </div>
        </div>
    );
};

export default BTicino;
